import { useInstallIntegration } from "@/api/useIntegrations"
import { Form, Input, Password, Text } from "@/atoms"
import { INTEGRATION_VENDORS } from "@/const/vendors-constants"
import { getDisplayMessage, getFieldErrors } from "@/helpers/error.helper"
import { Trans, useTranslation } from "@/hooks/useTranslation"
import { AzurePatSteps } from "@/pages/IntegrationsPage/components/molecules/AzurePatSteps"
import { ConnectPageHeading } from "@/pages/IntegrationsPage/components/molecules/ConnectPageHeading"
import { IntegrationBreadcrumbHeader } from "@/pages/IntegrationsPage/components/molecules/IntegrationBreadcrumbHeader"
import { getTicketingSettings } from "@/pages/IntegrationsPage/components/organisms/TicketingConfigurationTab/ticketingSettings.selector"
import {
  AZURE_BOARDS_FIELDS,
  isValidAzureOrganization,
  toAzureBoardsInstallPayload,
  toFieldErrors
} from "@/pages/IntegrationsPage/helpers/ticketing"
import { useIntegration } from "@/pages/IntegrationsPage/hooks/useIntegration"
import { PageWithBreadcrumbTemplate } from "@/templates/PageWithBreadcrumbTemplate"
import { ConnectIntegrationFooter } from "../ConnectIntegrationPage/ConnectIntegrationFooter"

import type { AzureBoardsFormValues } from "./AzureBoardsIntegrationPage.types"

const FORM_ID = "azure-boards-form"

export const AzureBoardsIntegrationPage = () => {
  const vendor = INTEGRATION_VENDORS.azureBoards
  const [data, actions] = useIntegration({ vendor })
  const { form, t: tRoot, message, isDirty, onFieldsChange, focusRef } = actions
  const { t } = useTranslation("translation", {
    keyPrefix: "integrations.azure_boards"
  })
  const { t: tSystems } = useTranslation("translation", {
    keyPrefix: "ticketing.systems"
  })
  const { t: tSteps } = useTranslation("translation", {
    keyPrefix: "integrations.connect.azure.steps"
  })
  const installer = useInstallIntegration({ vendor })
  const title = t("title")
  // Connecting again only replaces the token of the connected organization
  const { account } = getTicketingSettings(data?.configuration, vendor)
  const description = account
    ? t("form.reconnectDescription", { account })
    : undefined

  const onFinish = async (values: AzureBoardsFormValues) => {
    try {
      await installer.mutateAsync({ data: toAzureBoardsInstallPayload(values) })
    } catch (error) {
      const fieldErrors = toFieldErrors(
        AZURE_BOARDS_FIELDS,
        getFieldErrors(error)
      )

      if (fieldErrors.length) form.setFields(fieldErrors)
      else message.error(getDisplayMessage(error) ?? t("messages.failed"))
      return
    }

    message.success(t("messages.success"))
    actions.backToIntegration(true)
  }

  return (
    <PageWithBreadcrumbTemplate
      full
      header={
        <IntegrationBreadcrumbHeader
          vendorName={tSystems("azure_boards")}
          vendorUrl={actions.getBackToIntegrationUrl()}
          title={title}
        />
      }
      footer={
        <ConnectIntegrationFooter
          disabled={!isDirty}
          isLoading={installer.isPending}
          okProps={{ form: FORM_ID, htmlType: "submit" }}
          onCancel={() => actions.backToIntegration()}
        />
      }
    >
      <ConnectPageHeading title={title} description={description} />
      <Form
        form={form}
        id={FORM_ID}
        layout="vertical"
        onFieldsChange={onFieldsChange}
        onFinish={onFinish}
      >
        <AzurePatSteps
          instructions={[
            t("form.instructions.openTokens"),
            tSteps("generate.2"),
            tSteps("generate.3"),
            t("form.instructions.selectOrganization"),
            tSteps("generate.5")
          ]}
          scopes={[t("form.scopes.workItems"), t("form.scopes.projectAndTeam")]}
          note={t("form.tokenUserNote")}
        >
          <Form.Item
            label={
              <Text type="secondary">
                <Trans>{tSteps("organizationName.description")}</Trans>
              </Text>
            }
            colon={false}
            name="organization"
            required={false}
            rules={[
              {
                required: true,
                whitespace: true,
                message: tRoot("error.requiredField")
              },
              {
                validator: (_: unknown, value?: string) =>
                  !value?.trim() || isValidAzureOrganization(value)
                    ? Promise.resolve()
                    : Promise.reject(new Error(t("form.organization.invalid")))
              }
            ]}
          >
            <Input
              ref={focusRef}
              autoComplete="off"
              placeholder={tSteps("organizationName.placeholder")}
            />
          </Form.Item>
          <Form.Item
            label={
              <Text type="secondary">{tSteps("accessToken.description")}</Text>
            }
            colon={false}
            name="pat"
            required={false}
            rules={[
              {
                required: true,
                whitespace: true,
                message: tRoot("error.requiredField")
              }
            ]}
          >
            <Password
              autoComplete="new-password"
              placeholder={tSteps("accessToken.placeholder")}
              visibilityToggle={false}
            />
          </Form.Item>
        </AzurePatSteps>
      </Form>
    </PageWithBreadcrumbTemplate>
  )
}
